import {
  getIngredientConsumptionQuantity,
  getIngredientMenuBaseQuantity,
} from "./menuIngredientModel";

const toSafeNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeKitchenRoutingText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const isExplicitKitchenRoutingValue = (value, expected) => {
  if (typeof value === "boolean") {
    return value === expected;
  }

  return normalizeKitchenRoutingText(value) === String(expected);
};

export const isBeverageCategory = (value) => {
  const normalizedValue = normalizeKitchenRoutingText(value);
  const categoryWords = normalizedValue.split(/[^a-z0-9]+/).filter(Boolean);

  return categoryWords.some((word) =>
    [
      "bebida",
      "bebidas",
      "trago",
      "tragos",
      "drink",
      "drinks",
      "beverage",
      "beverages",
      "gaseosa",
      "gaseosas",
      "cerveza",
      "cervezas",
      "vino",
      "vinos",
      "cocktail",
      "cocktails",
      "coctel",
      "cocteles",
      "aperitivo",
      "aperitivos",
      "jugo",
      "jugos",
      "licuado",
      "licuados",
      "smoothie",
      "smoothies",
      "infusion",
      "infusiones",
      "te",
      "tes",
      "agua",
      "aguas",
      "soda",
      "sodas",
      "refresco",
      "refrescos",
    ].includes(word)
  );
};

export const shouldOrderItemGoToKitchen = (item) => {
  if (!item) {
    return false;
  }

  if (
    isExplicitKitchenRoutingValue(item?.sendToKitchen, false) ||
    isExplicitKitchenRoutingValue(item?.kitchenRequired, false)
  ) {
    return false;
  }

  if (
    isExplicitKitchenRoutingValue(item?.sendToKitchen, true) ||
    isExplicitKitchenRoutingValue(item?.kitchenRequired, true)
  ) {
    return true;
  }

  return ![
    item?.categoryName,
    item?.category,
    item?.categoriaNombre,
    item?.subcategoryName,
    item?.subcategory,
    item?.subcategoria,
  ].some(isBeverageCategory);
};

export const filterKitchenOrderItems = (items = []) =>
  (Array.isArray(items) ? items : []).filter(shouldOrderItemGoToKitchen);

export const filterDirectDeliveryOrderItems = (items = []) =>
  (Array.isArray(items) ? items : []).filter(
    (item) => !shouldOrderItemGoToKitchen(item)
  );

export const orderRequiresKitchen = (order) =>
  filterKitchenOrderItems(order?.items).length > 0;

export const orderHasDirectDeliveryItems = (order) =>
  filterDirectDeliveryOrderItems(order?.items).length > 0;

const OPERATIONAL_KITCHEN_MODES = new Set(["manual", "screen", "printer"]);
const KITCHEN_BOARD_PREPARING_STATUSES = new Set([
  "screen_confirmed",
  "screen_ready",
  "printer_printed",
  "printer_ready",
]);
const KIOSK_PRINTER_VISIBLE_STATUSES = new Set([
  "printer_printed",
  "printer_ready",
  "screen_confirmed",
  "screen_ready",
]);
const EXTERNAL_KITCHEN_BOARD_STATUSES = new Set([
  "manual_confirmed",
  "screen_confirmed",
  "screen_ready",
]);

const normalizeOperationalKitchenMode = (value) => {
  const normalizedValue = String(value || "").trim().toLowerCase();
  return OPERATIONAL_KITCHEN_MODES.has(normalizedValue) ? normalizedValue : "";
};

export const resolveOrderKitchenModeAtDispatch = ({
  order,
  profile,
  branch,
  restaurant,
} = {}) => {
  const orderMode = normalizeOperationalKitchenMode(order?.kitchenModeSnapshot);

  // Screen and printer are explicit routing decisions captured when the order
  // was created. A stale local "manual" profile must never overwrite them.
  if (orderMode === "screen" || orderMode === "printer") {
    return orderMode;
  }

  const configuredMode = [
    branch?.kitchenMode,
    branch?.kitchen_mode,
    profile?.kitchenMode,
    profile?.kitchen_mode,
    restaurant?.kitchenMode,
    restaurant?.kitchen_mode,
  ]
    .map(normalizeOperationalKitchenMode)
    .find((mode) => mode === "screen" || mode === "printer");

  return configuredMode || orderMode || "manual";
};

const isKioskPrinterKitchenOrder = (order) =>
  String(order?.source || "").trim().toLowerCase() === "kiosk" &&
  normalizeOperationalKitchenMode(order?.kitchenModeSnapshot) === "printer";

const normalizeKitchenBoardPhase = (value) => {
  const phase = String(value || "").trim().toLowerCase();

  if (phase === "confirmed" || phase === "preparing") {
    return "preparation";
  }

  if (
    [
      "ready_for_pickup",
      "ready_for_delivery",
      "assigned_to_driver",
      "out_for_delivery",
    ].includes(phase)
  ) {
    return "ready";
  }

  return phase;
};

export const shouldShowOperationalOrderOnKitchenBoard = (
  order,
  { activeKitchenMode = "" } = {}
) => {
  if (!order || !orderRequiresKitchen(order)) {
    return false;
  }

  const phase = normalizeKitchenBoardPhase(order.phase);
  const orderKitchenMode =
    normalizeOperationalKitchenMode(order.kitchenModeSnapshot) || "manual";
  const currentKitchenMode = normalizeOperationalKitchenMode(activeKitchenMode);
  const kitchenStatus = String(order.kitchenDispatchStatus || "")
    .trim()
    .toLowerCase();

  if (phase === "new" || phase === "closed" || phase === "cancelled") {
    return false;
  }

  if (
    orderKitchenMode === "screen" ||
    (currentKitchenMode === "screen" && ["preparation", "ready"].includes(phase))
  ) {
    return true;
  }

  const isExplicitExternalKitchenDispatch =
    String(order.source || "").trim().toLowerCase() === "external" &&
    EXTERNAL_KITCHEN_BOARD_STATUSES.has(kitchenStatus);

  if (isExplicitExternalKitchenDispatch && ["preparation", "ready"].includes(phase)) {
    return true;
  }

  return (
    isKioskPrinterKitchenOrder(order) &&
    (phase === "ready" ||
      (phase === "preparation" && KIOSK_PRINTER_VISIBLE_STATUSES.has(kitchenStatus)))
  );
};

export const getOperationalOrderKitchenStage = (
  order,
  { activeKitchenMode = "" } = {}
) => {
  if (!shouldShowOperationalOrderOnKitchenBoard(order, { activeKitchenMode })) {
    return "other";
  }

  const phase = normalizeKitchenBoardPhase(order?.phase);

  if (phase === "ready") {
    return "ready";
  }

  if (phase !== "preparation") {
    return "other";
  }

  const orderKitchenMode =
    normalizeOperationalKitchenMode(order?.kitchenModeSnapshot) || "manual";
  const currentKitchenMode = normalizeOperationalKitchenMode(activeKitchenMode);
  const kitchenStatus = String(order?.kitchenDispatchStatus || "")
    .trim()
    .toLowerCase();
  const isExplicitExternalKitchenDispatch =
    String(order?.source || "").trim().toLowerCase() === "external" &&
    EXTERNAL_KITCHEN_BOARD_STATUSES.has(kitchenStatus);
  const belongsToScreenFlow =
    orderKitchenMode === "screen" ||
    currentKitchenMode === "screen" ||
    isExplicitExternalKitchenDispatch;

  if (belongsToScreenFlow) {
    return KITCHEN_BOARD_PREPARING_STATUSES.has(kitchenStatus)
      ? "preparing"
      : "new";
  }

  return "preparing";
};

const parseLegacyOrderName = (value) => {
  const rawValue = String(value || "").trim();
  const match = rawValue.match(/^(\d+)\s+(.+)$/);

  if (!match) {
    return {
      quantity: 1,
      productName: rawValue,
    };
  }

  return {
    quantity: Math.max(1, Number(match[1] || 1)),
    productName: match[2] || rawValue,
  };
};

export const buildOperationalItemLabel = (
  quantity,
  productName,
  selectionDetails = []
) => {
  const safeQuantity = Math.max(1, Number(quantity || 1));
  const safeProductName = String(productName || "").trim() || "Producto";
  const details = Array.isArray(selectionDetails)
    ? selectionDetails.filter(Boolean)
    : [];

  if (!details.length) {
    return `${safeQuantity} ${safeProductName}`;
  }

  return `${safeQuantity} ${safeProductName} (${details.join(", ")})`;
};

export const normalizeOperationalOrderItem = (item) => {
  const legacy = parseLegacyOrderName(item?.nombre || item?.name || "");
  const quantity = Math.max(1, toSafeNumber(item?.quantity, legacy.quantity || 1));
  const productName =
    item?.productName ||
    item?.sourceProductName ||
    item?.name ||
    legacy.productName ||
    "Producto";
  const selectionDetails = Array.isArray(item?.selectionDetails)
    ? item.selectionDetails.filter(Boolean)
    : [];
  const baseUnitPrice = toSafeNumber(
    item?.baseUnitPrice ?? item?.basePrice ?? item?.unitPrice,
    0
  );
  const extraUnitCost = toSafeNumber(
    item?.extraUnitCost ?? item?.extraCost,
    Math.max(0, toSafeNumber(item?.unitPrice, baseUnitPrice) - baseUnitPrice)
  );
  const unitPrice = toSafeNumber(
    item?.unitPrice,
    baseUnitPrice + extraUnitCost
  );
  const productId = item?.productId || item?.sourceProductId || "";
  const delivered = item?.delivered === true;
  const sendToKitchen = shouldOrderItemGoToKitchen(item);

  return {
    ...item,
    id: item?.id || `order-item-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
    productId,
    sourceProductId: productId,
    productName,
    sourceProductName: productName,
    name: item?.name || productName,
    quantity,
    baseUnitPrice,
    extraUnitCost,
    unitPrice,
    delivered,
    deliveredAt: delivered ? item?.deliveredAt || "" : "",
    deliveredBy: delivered ? item?.deliveredBy || "" : "",
    deliveryStatus: delivered
      ? "delivered"
      : String(item?.deliveryStatus || "pending").trim().slice(0, 40),
    deliveryAssignedAt: item?.deliveryAssignedAt || "",
    assignedWaiterUserId: item?.assignedWaiterUserId || "",
    assignedWaiterName: String(item?.assignedWaiterName || "").trim().slice(0, 80),
    consumos: Array.isArray(item?.consumos) ? item.consumos : [],
    selection:
      item?.selection && typeof item.selection === "object" ? item.selection : {},
    selectionDetails,
    imageUrl: item?.imageUrl || "",
    signature: item?.signature || "",
    sendToKitchen,
    nombre: buildOperationalItemLabel(quantity, productName, selectionDetails),
  };
};

export const normalizeOperationalOrderItems = (items = []) =>
  (Array.isArray(items) ? items : []).map((item) =>
    normalizeOperationalOrderItem(item)
  );

export const createOperationalOrderItem = (payload) =>
  normalizeOperationalOrderItem(payload);

const getProductIngredientEntries = (product) => {
  if (Array.isArray(product?.ingredients)) {
    return product.ingredients;
  }

  if (Array.isArray(product?.ingredientes)) {
    return product.ingredientes;
  }

  return [];
};

export const buildOperationalConsumptions = (
  product,
  { selection = null, quantity = 1, quantityScale = 1 } = {}
) => {
  const safeQuantity =
    Math.max(0, toSafeNumber(quantity, 1)) *
    Math.max(0, toSafeNumber(quantityScale, 1));

  return getProductIngredientEntries(product)
    .map((ingredient) => {
      const ingredientId =
        ingredient?.id || ingredient?.ingredientId || ingredient?.ingredient_id || "";
      const baseQuantity = getIngredientMenuBaseQuantity(ingredient);
      const consumptionQuantity = getIngredientConsumptionQuantity(ingredient);
      const selectedQuantity = toSafeNumber(
        selection && ingredientId ? selection?.[ingredientId] : undefined,
        baseQuantity
      );

      if (selectedQuantity <= 0 || consumptionQuantity <= 0) {
        return null;
      }

      const visibleQuantityScale =
        baseQuantity > 0 ? selectedQuantity / baseQuantity : selectedQuantity;
      const totalConsumption =
        visibleQuantityScale * consumptionQuantity * safeQuantity;

      if (totalConsumption <= 0) {
        return null;
      }

      return {
        id: ingredientId,
        ingredientId,
        ingredientName: ingredient?.name || ingredient?.nombre || "",
        unit: ingredient?.consumptionUnit || ingredient?.unit || ingredient?.unidad || "un",
        unidad: ingredient?.unidad || ingredient?.unit || ingredient?.consumptionUnit || "un",
        quantity: Number(totalConsumption.toFixed(4)),
        cantidad: Number(totalConsumption.toFixed(4)),
      };
    })
    .filter(Boolean);
};

export const scaleOperationalConsumptions = (
  consumos = [],
  currentQuantity = 1,
  nextQuantity = 1
) => {
  const safeCurrentQuantity = Math.max(1, toSafeNumber(currentQuantity, 1));
  const safeNextQuantity = Math.max(1, toSafeNumber(nextQuantity, 1));

  return (Array.isArray(consumos) ? consumos : []).map((consumo) => {
    const currentConsumption = toSafeNumber(
      consumo?.quantity ?? consumo?.cantidad,
      0
    );

    return {
      ...consumo,
      quantity: Number(
        ((currentConsumption / safeCurrentQuantity) * safeNextQuantity).toFixed(4)
      ),
      cantidad: Number(
        ((currentConsumption / safeCurrentQuantity) * safeNextQuantity).toFixed(4)
      ),
    };
  });
};
